import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb'
import { useNavigate } from '@tanstack/react-router'
import { Button } from "@/components/ui/button"
import { ArrowUpRight, ChevronRight, Banknote, Plus, NotepadText } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useState } from 'react'
import {Route as supplierOneRoute} from '../index'
import dayjs from 'dayjs'
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from '@/utils/currency_converter'

const ReUse1 = ({
  label,
  value
}: {
  label: string,
  value: string
}) => {
  return (
    <div className='flex items-center justify-between text-sm mb-1'>
      <p className='font-light text-gray-500'>{label}</p>
      <p className='text-gray-600'>{value}</p>
    </div>
  )
}

const ReUse2 = ({
  label,
  value,
  link
}: {
  label: string,
  value: string | React.ReactNode,
  link: string
}) => {
  return (
    <div className='flex justify-between bg-[#ECEFF8] rounded-2xl p-4'>
      <div>
        <p className='text-sm text-gray-500'>{label}</p>
        <p className='text-xl mt-1'>{value}</p>
      </div>
      {/* <p className='text-blue-600 text-sm font-light'>View</p> */}
    </div>
  )
}

const Component = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('all')
  const {supplier: data, purchaseInvoices} = supplierOneRoute.useLoaderData()
  
  return (
    <div className="h-[calc(100vh-40px)] pb-16 flex flex-col">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
              <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => {
                    navigate({
                      to: '/suppliers',
                    })
                  }} style={{
                    cursor: 'pointer'
                  }}>Suppliers</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                  <BreadcrumbPage>{data.supplier?.name}</BreadcrumbPage>
              </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className="my-4" />

      <div className="flex-1 flex overflow-hidden">
        {/* Left Half */}
        <div className="w-1/4 overflow-y-auto pr-2">
          <div className='bg-[#00000005] p-4 rounded-md'>
            <p className='text-3xl font-semibold'>Supplier Info</p>
            <p className='mt-6 font-semibold text-lg'>Supplier Details</p>
            <hr className='my-3'/>
            <ReUse1 label='Full Name' value={data.supplier?.metadata?.contactPersonName!} />
            <ReUse1 label='Contact Number' value={data.supplier?.metadata?.phoneNumber!} />
            <hr className='my-3'/>
            <ReUse1 label='Credit Allowed' value={data.supplier?.creditTerms?.isCreditAllowed ? 'Yes' : 'No'} />
            {data.supplier?.creditTerms?.isCreditAllowed && <ReUse1 label='Credit Period' value={data.supplier?.creditTerms?.creditPeriod.toString()!} />}
            {data.supplier?.creditTerms?.isCreditAllowed && <ReUse1 label='Monthly Payment Day' value={data.supplier?.creditTerms?.monthlyPaymentDay.toString()!} />}
          </div>

          <div className='flex flex-col space-y-4 mt-6'>
            <p className='font-semibold text-lg'>Lifetime Stats</p>
            <hr className='my-3'/>
            <ReUse2 label='Total Inventory Purchased' value={convertToCurrencyRounded(data.lifetimeStats?.totalCapitalizationOfGoods!)} link='"' />
            <ReUse2 label='Active Inventory' value={data.lifetimeStats?.availableQuantity.toString()!} link='"' />
            <ReUse2 label='Expired Inventory' value={data.lifetimeStats?.expiredQuantity.toString()!} link='"' />
            <ReUse2 label='Out of Stock Inventory' value={data.lifetimeStats?.outOfStockQuantity.toString()!} link='"' />
            <ReUse2 label='Total POs' value={data.lifetimeStats?.totalPurchaseOrders.toString()!} link='"' />
            <ReUse2 label='Total PIs' value={data.lifetimeStats?.totalPurchaseInvoices.toString()!} link='"' />
            <ReUse2 label='Total Returns' value={data.lifetimeStats?.totalPurchaseReturns.toString()!} link='"' />
            <ReUse2 label='Payments Made' value={data.lifetimeStats?.totalPaymentsMade.toString()!} link='"' />
            <ReUse2 label='Upcoming Payments' value={data.lifetimeStats?.totalUpcomingPayments.toString()!} link='"' />
            <ReUse2 label='Business Value' value={convertToCurrencyRounded(data.lifetimeStats?.totalValueOfPaymentsMade!)} link='"' />
          </div>
        </div>

        {/* Right Half */}
        <div className='w-3/4 overflow-y-auto pr-2'>
          <div className='flex items-center justify-between'>
            <p className='text-3xl font-semibold'>Outgoing payments</p>
            <div className='flex gap-2'>
              <Button variant="outline" className="flex gap-2 shadow-sm" onClick={() => {
              navigate({
                to: `/suppliers/${data.supplier?.id}/supplier-credit-notes`,
              })
            }}>
              <NotepadText className="h-6 w-6" />
              Credit Notes
            </Button>
              <Button variant="outline" className="flex gap-2 shadow-sm bg-blue-600 text-white" onClick={() => {
              navigate({
                to: `/suppliers/${data.supplier?.id}/make-payment`,
              })
            }}>
              <Banknote className="h-6 w-6" />
              Make Payment
            </Button>
            </div>
          </div>
          <p className='text-lg mt-6 text-gray-600 font-semibold'>Available to pay out this month</p>
          <hr className='my-3' />
          {
            data.payouts.filter((item) => item.amountDue > 0).map((item) => (
              <div className='flex items-center justify-between my-4'>
                <p className='text-blue-600'>Payout on {
                  dayjs(item.dueDate?.toDate()!).format('MMMM D, YYYY') + " "
                } for Invoice {item.invoiceNumber}</p>
                <p className='text-sm text-gray-500'>{convertToCurrencyRounded(item.amountDue)}</p>
              </div>
            ))
          }
          <p className='mt-6 text-gray-500 font-semibold'>Still pending payouts to be made for this supplier</p>
          <hr className='mt-3 '/>
          {
            data.payouts.filter((item) => item.amountDue > 0).map((item) => (
              <div className='flex items-center justify-between my-4'>
                <p className='text-blue-600'>Payout on {
                  dayjs(item.dueDate?.toDate()).format('MMMM D, YYYY') + " "
                } for Invoice {item.invoiceNumber}</p>
                <p className='text-sm text-gray-500'>{convertToCurrencyRounded(item.amountDue)}</p>
              </div>
            ))
          }

          <div className='mt-8'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-3xl font-semibold'>Invoices</h2>
              <div className='flex gap-2'>
                {/* <Button size="sm" variant="outline" className="flex gap-2 shadow-sm">
                  <ArrowUpRight className="h-6 w-6" />
                  Export All
                </Button>
                <Button size="sm" className="flex gap-2 bg-blue-600">
                  <Plus className="h-6 w-6" />
                  Repeat Invoice
                </Button> */}
              </div>
            </div>

            <div className="flex gap-3 border-b mt-6 mb-2">
              <button 
                className={`pb-2 px-2 text-sm font-medium ${
                  activeTab === 'all' 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-500'
                }`}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
              <button 
                className={`pb-2 px-2 text-sm font-medium ${
                  activeTab === 'succeeded' 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-500'
                }`}
                onClick={() => setActiveTab('succeeded')}
              >
                Paid
              </button>
              <button 
                className={`pb-2 px-2 text-sm font-medium ${
                  activeTab === 'refunded' 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-500'
                }`}
                onClick={() => setActiveTab('refunded')}
              >
                Unpaid
              </button>
            </div>

            <Table>
              <TableHeader>
                <TableRow className='text-sm'>
                  <TableHead className="w-[50px]"><Checkbox /></TableHead>
                  <TableHead>AMOUNT</TableHead>
                  <TableHead>INVOICE DATE</TableHead>
                  <TableHead>DUE DATE</TableHead>
                  <TableHead>INVOICE NO.</TableHead>
                  <TableHead>UNIQUE SKU'S</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchaseInvoices.invoices.map((invoice, i) => (<TableRow key={i}>
                  <TableCell><Checkbox /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>
                          {convertToCurrencyRounded(invoice.totalAmount)}
                      </span>
                      {invoice.isPaid && <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">Paid</span>}
                      {!invoice.isPaid && <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">Unpaid</span>}
                    </div>
                  </TableCell>
                  <TableCell>{
                    dayjs(invoice.invoiceDate?.toDate()).format('MMMM D, YYYY')
                    }</TableCell>
                  <TableCell>{
                    dayjs(invoice.dueDate?.toDate()).format('MMMM D, YYYY')
                    }</TableCell>
                  <TableCell>Invoice {invoice.invoiceNumber}</TableCell>
                  <TableCell>{invoice.totalItems}</TableCell>
                  <TableCell>
                  <Button variant="outline" size="sm" className="px-2 flex items-center gap-1 text-sm" onClick={() => {
                    navigate({
                      to: `/inventory/purchase-invoice/${invoice.id}`,
                    })
                  }}>
                    <ChevronRight className="h-4 w-4" />
                    More details  
                    </Button>
                  </TableCell>
                </TableRow>))}
              </TableBody>
            </Table>
            
          </div>
        </div>
      </div>
    </div>
  )
}

export default Component